import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, SERVICES, CONTACTS, COMPANY } from "@/data/site";

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative navy-gradient grain text-white overflow-hidden">
      <div className="relative ssa-container py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo variant="dark" testId="site-footer-logo" />
            <p className="mt-6 text-sm leading-relaxed text-white/70 max-w-sm">
              Integrated maritime logistics, freight forwarding, vessel chartering and supply chain
              solutions — trusted across international trade corridors since {COMPANY.since}.
            </p>
            <div className="mt-6 eyebrow text-turquoise" style={{ letterSpacing: "0.28em" }}>
              {COMPANY.tagline}
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-2" data-testid="site-footer-links">
            <h4 className="text-sm font-semibold text-white mb-5">Company</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/70 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold text-white mb-5">Services</h4>
            <ul className="grid grid-cols-1 gap-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-semibold text-white mb-5">Head Office</h4>
            <div className="flex items-start gap-3 text-sm text-white/70">
              <MapPin className="h-4 w-4 mt-0.5 text-turquoise shrink-0" />
              <div className="leading-relaxed">
                {CONTACTS.office.lines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </div>
            <a
              href={`tel:${COMPANY.officePhoneTel}`}
              className="mt-5 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4 text-turquoise" /> {COMPANY.officePhone}
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="mt-3 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors break-all"
            >
              <Mail className="h-4 w-4 text-turquoise" /> {COMPANY.email}
            </a>
            <div>
              <a
                href={`mailto:${COMPANY.email}`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-turquoise hover:text-white transition-colors"
              >
                Get in touch <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/55">
            © {year} {COMPANY.name}. All rights reserved.
          </p>
          <p className="text-xs text-white/45">
            Maritime Logistics · Freight Forwarding · Supply Chain Solutions
          </p>
        </div>
      </div>
    </footer>
  );
};
